import {
  Breadcrumb,
  InfoLabel,
  Modal,
  ProgressIndicator
} from '@/components/atoms';
import {
  AccordionItem,
  ImageUploadField,
  PageHeader,
  SaveCancelFooter
} from '@/components/molecules';
import {MediaPreviewWrapper, PageContainer} from '@/components/organisms';
import {MEDIA_PREVIEW_CARD} from '@/components/organisms/MediaPreview/mediaPreviewConfig';
import {SalesTargetDisplayInformationSection} from '@/containers/DisplayContent/SalesTargetDisplayInformationSection';
import {
  SALES_TARGET_SIMPLE_IMAGE_MAX_SIZE_BYTES,
  SALES_TARGET_SIMPLE_IMAGE_TYPES,
  buildTargetImageDescription
} from '@/containers/DisplayContent/salesTargetSimpleDisplayShared';
import {useSalesTargetSimpleDisplayContent} from '@/hooks/useSalesTargetSimpleDisplayContent';
import {ROUTES} from '@/utils/constants/routesConstants';
import {MODAL_TYPE, FONT_SIZE} from '@/utils/constants/globalStyle';
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants';

// Rack Challenge image rules stay page-local so CDA changes cannot alter them.
const TARGET_IMAGE_DIMENSIONS = {width: 72, height: 218};
const TARGET_IMAGE_DESCRIPTION = buildTargetImageDescription(
  TARGET_IMAGE_DIMENSIONS,
  SALES_TARGET_SIMPLE_IMAGE_MAX_SIZE_BYTES
);

export const RackChallengeDisplayContent = () => {
  const {
    languages,
    previewImage,
    displayContent,
    isCancel,
    imageExpanded,
    setImageExpanded,
    disableDraft,
    stepConfig,
    handleImageChange,
    handleImageDelete,
    handleContentChange,
    handleCancel,
    onConfirmCancel,
    handleDraftAction,
    handleNextAction,
    handleStepClick,
    getFormError
  } = useSalesTargetSimpleDisplayContent({
    targetType: TARGET_TYPES.RackChallenge,
    draftErrorMessage: 'Error saving rack challenge display draft:'
  });

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {href: ROUTES.HOME, label: ''},
            {href: ROUTES.SALES_TARGETS, label: 'Loyalty Campaigns'},
            {href: ROUTES.SALES_TARGETS, label: 'Add Sales Target'}
          ]
        }}
        id='rcdc_bradcrumb'
      />

      <PageHeader title='Add Sales Target' />

      <ProgressIndicator
        currentStep={2}
        completed={false}
        orientation='horizontal'
        steps={stepConfig.steps}
        titles={stepConfig.titles}
        className='mb-6'
        onStepClick={handleStepClick}
      />
      <br />

      <PageContainer data-testid='rack-challenge-media'>
        <div className='rack-challenge-media-accordion'>
          <AccordionItem
            isDark
            expanded={imageExpanded}
            index={0}
            label='Display Image'
            onUpdate={() => setImageExpanded((prev) => !prev)}>
            <div className='w-full pt-4'>
              <h3 className='mb-1 text-base font-bold'>Target Image*</h3>

              <div
                className='mb-4 grid gap-6 rounded-lg border border-[color:var(--color-border-default)] p-4 md:grid-cols-2'
                data-testid='target-image-row'>
                <div>
                  <ImageUploadField
                    label='Upload file'
                    tooltipText={TARGET_IMAGE_DESCRIPTION}
                    description={TARGET_IMAGE_DESCRIPTION}
                    imageUri={previewImage}
                    acceptedTypes={SALES_TARGET_SIMPLE_IMAGE_TYPES}
                    dimensions={TARGET_IMAGE_DIMENSIONS}
                    validateDimensions={false}
                    validateAspectRatio={false}
                    maxSizeBytes={SALES_TARGET_SIMPLE_IMAGE_MAX_SIZE_BYTES}
                    onChange={handleImageChange}
                    onDelete={handleImageDelete}
                    dataTestId='target-image-upload'
                    containerClassName='w-full rounded-lg border border-[color:var(--color-border-default)] p-3'
                  />
                </div>
                <div>
                  <p className='mb-3 text-sm font-bold'>Image Preview in App</p>
                  <MediaPreviewWrapper
                    types={[MEDIA_PREVIEW_CARD]}
                    scale={0.55}
                    content={[{image: previewImage || undefined}]}
                  />
                </div>
              </div>

              <InfoLabel title='No text in the image' />
            </div>
          </AccordionItem>
        </div>
      </PageContainer>

      <PageContainer data-testid='rack-challenge-display-content'>
        <PageHeader title='Display Information' fontSize={FONT_SIZE.LG} />
        <SalesTargetDisplayInformationSection
          languages={languages}
          content={displayContent}
          onContentChange={handleContentChange}
          targetTypeLabel={TARGET_TYPES.RackChallenge}
        />
      </PageContainer>

      {isCancel && (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={handleCancel}
          title='Are you sure you want to cancel?'
          text='Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'
          showPrimaryButton
          primaryButtonText='Yes'
          onClickPrimaryButton={onConfirmCancel}
          showSecondaryButton
          secondaryButtonText='No'
          onClickSecondaryButton={handleCancel}
          footerProps={{size: 'medium', type: 'primary'}}
          isDark
        />
      )}

      <SaveCancelFooter
        FormError={getFormError()}
        handleCancel={handleCancel}
        draftAction={handleDraftAction}
        nextAction={handleNextAction}
        disableDraft={disableDraft}
      />
    </>
  );
};
